import { gql } from '@apollo/client';
import { Alert } from 'reactstrap';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryWithMsg } from '../utils/hooks';
import { getTeamBillingPath } from '../utils/team';

const FarmSubscriptionQuery = gql`
  query FarmSubscription($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      isArchived
      isLocked
      owner {
        id
      }
      team {
        id
        name
        hasSubscription
        allowedActions {
          id
          canAccess
          canUpdateSettings
        }
      }
    }
    me {
      id
      teams {
        id
        name
        hasSubscription
        allowedActions {
          id
          canUpdateSettings
        }
      }
    }
  }
`;

const TeamSubscriptionInfo = ({ team }) => {
  const canUpdateTeamSettings = team.allowedActions.canUpdateSettings;
  return canUpdateTeamSettings ? (
    <Link to={getTeamBillingPath(team)}>
      Update {team.name} billing to get edit access
    </Link>
  ) : team.allowedActions.canAccess ? (
    <Link to={`/app/team/${team.id}/members`}>
      Contact a {team.name} admin to update billing
    </Link>
  ) : (
    <span>Contact an owner to update billing.</span>
  );
};

const IndividualSubscriptionInfo = ({ user }) => {
  const teamsWithAdmin = (user?.teams || []).filter(
    (team) => team.allowedActions.canUpdateSettings,
  );
  const team = teamsWithAdmin[0];

  return (
    <>
      {/* There are many possible permutaions of team membership and
    statuses for a given user. This handles a few common ones. */}
      <Link to={`/app/onboard?backPath=${window.location.pathname}`}>
        Create a team
      </Link>{' '}
      {teamsWithAdmin.length === 1 && (
        <span>
          or{' '}
          <Link to={getTeamBillingPath(team)}>
            update {teamsWithAdmin[0].name} billing
          </Link>{' '}
        </span>
      )}
      {teamsWithAdmin.length > 1 && <span>or update team billing </span>}
      <span>for edit access.</span>
    </>
  );
};

const SubscriptionInfoAlert = styled(Alert)`
  width: calc(
    100vw - 2 * var(--side-panel-width) - 2 * var(--content-x-padding)
  );
  a {
    color: var(--overyield-blue);
    font-weight: bold;
  }
`;

const SubscriptionInfoBar = () => {
  const { farmId } = useParams();
  const { data, msg } = useQueryWithMsg(FarmSubscriptionQuery, {
    variables: { farmId },
  });
  const userIsOwner =
    data?.farm?.owner?.id != null && data?.farm?.owner?.id === data?.me?.id;
  const hasTeam = !!data?.farm?.team?.id;
  const { isArchived, isLocked } = data?.farm || {};
  return (
    <SubscriptionInfoAlert className="mb-0 py-2" color="dark">
      {msg ? (
        <span>{msg}</span>
      ) : (
        <span>
          This farm is currently view-only.{' '}
          {isArchived ? (
            'Unarchive to restore editing.'
          ) : isLocked ? (
            userIsOwner ? (
              'Unlock to restore editing.'
            ) : (
              'Contact the owner to unlock.'
            )
          ) : hasTeam ? (
            <TeamSubscriptionInfo team={data.farm.team} />
          ) : (
            <IndividualSubscriptionInfo user={data.me} />
          )}
        </span>
      )}
    </SubscriptionInfoAlert>
  );
};

export default SubscriptionInfoBar;
